import { resolveImagePath } from '@helpers/gallery';
import geotabShare from '@images/geotabShareImage.jpg';
import geotabLogoWithR from '@images/Navigation/geotab-logo-with-r-600x60.svg';

const getFaqArray = (faqContents) => {
    return Array.isArray(faqContents)
        ? faqContents.map(({ answer, question }) => {
            return {
                '@type': 'Question',
                name: question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: answer,
                },
            };
        })
        : {};
};

export const getBlogStructuredDataScript = (article, mediaUrlPrefix, prefixStringWithBaseUrl, fullUrl) => {
    const hyperTocJsonLd = getHyperTocStructuredData(article.htmlContent, fullUrl);

    const blogPostingJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': fullUrl,
        },
        headline: article.title ? article.title.replace(/"/g, '\\"') : article.pageTitle.replace(/"/g, '\\"'),
        image: getImageArray(article, mediaUrlPrefix, prefixStringWithBaseUrl),
        datePublished: article.publishedDateUtc,
        dateModified: article.modifiedDateUtc,
        author: {
            '@type': 'Person',
            name: article.author && article.author.name.replace(/"/g, '\\"'),
        },
        publisher: {
            '@type': 'Organization',
            name: 'Geotab',
            logo: {
                '@type': 'ImageObject',
                url: prefixStringWithBaseUrl(geotabLogoWithR),
            },
        },
    };

    if (article?.frequentlyAskedQuestions?.length) {
        blogPostingJsonLd.mainEntity = {
            '@type': 'FAQPage',
            mainEntity: getFaqArray(article.frequentlyAskedQuestions),
        };
    }

    return {
        type: 'application/ld+json',
        body: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [blogPostingJsonLd, hyperTocJsonLd],
        }),
    };
};

export const getPressReleaseStructuredDataScript = (article, mediaUrlPrefix, prefixStringWithBaseUrl, fullUrl) => {
    return {
        type: 'application/ld+json',
        body: `{
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "url": "${fullUrl}",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "${fullUrl}"
            },
            "headline": "${
                article && article.title ? article.title.replace(/"/g, '') : article.pageTitle.replace(/"/g, '')
            }",
            "image": [${getImageArray(article, mediaUrlPrefix, prefixStringWithBaseUrl)}],
            "datePublished": "${article && article.publishedDateUtc}",
            "dateModified": "${article && article.modifiedDateUtc}",
            "publisher": {
              "@type": "Organization",
              "name": "Geotab",
              "logo": {
                "@type": "ImageObject",
                "url": "${prefixStringWithBaseUrl(geotabLogoWithR)}"
              }
            }
          }`,
    };
};

export const getSuccessStoryStructuredDataScript = (article, mediaUrlPrefix, prefixStringWithBaseUrl, fullUrl) => {
    const hyperTocJsonLd = getHyperTocStructuredData(article.htmlContent, fullUrl);
    const successStoryJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        url: fullUrl,
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': fullUrl,
        },
        name: article.title ? article.title.replace(/"/g, '\\"') : article.pageTitle.replace(/"/g, '\\"'),
        description: article.metaDescription
            ? article.metaDescription.replace(/"/g, '\\"')
            : article.description.replace(/"/g, '\\"'),
        headline: article.title ? article.title.replace(/"/g, '\\"') : article.pageTitle.replace(/"/g, '\\"'),
        image: getImageArray(article, mediaUrlPrefix, prefixStringWithBaseUrl),
        datePublished: article.publishedDateUtc,
        dateModified: article.modifiedDateUtc,
        publisher: {
            '@type': 'Organization',
            name: 'Geotab',
            logo: {
                '@type': 'ImageObject',
                url: prefixStringWithBaseUrl(geotabLogoWithR),
            },
        },
    };

    return {
        type: 'application/ld+json',
        body: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [successStoryJsonLd, hyperTocJsonLd],
        }),
    };
};

export const getGlossaryStructuredDataScript = (article, mediaUrlPrefix, prefixStringWithBaseUrl, fullUrl) => {
    const hyperTocJsonLd = getHyperTocStructuredData(article.htmlContent, fullUrl);

    const glossaryJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        url: fullUrl,
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': fullUrl,
        },
        name: article.title ? article.title.replace(/"/g, '\\"') : article.pageTitle.replace(/"/g, '\\"'),
        description: article.metaDescription
            ? article.metaDescription.replace(/"/g, '\\"')
            : article.description.replace(/"/g, '\\"'),
        headline: article.title ? article.title.replace(/"/g, '\\"') : article.pageTitle.replace(/"/g, '\\"'),
        image: getImageArray(article, mediaUrlPrefix, prefixStringWithBaseUrl),
        datePublished: article.publishedDateUtc,
        dateModified: article.modifiedDateUtc,
        publisher: {
            '@type': 'Organization',
            name: 'Geotab',
            logo: {
                '@type': 'ImageObject',
                url: prefixStringWithBaseUrl(geotabLogoWithR),
            },
        },
    };

    if (article?.frequentlyAskedQuestions?.length) {
        glossaryJsonLd.mainEntity = {
            '@type': 'FAQPage',
            mainEntity: getFaqArray(article.frequentlyAskedQuestions),
        };
    }

    return {
        type: 'application/ld+json',
        body: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [glossaryJsonLd, hyperTocJsonLd],
        }),
    };
};

const getImageArray = (article, mediaUrlPrefix, prefixStringWithBaseUrl) => {
    const imageArray = [];
    if (article && article.imageUrl) {
        imageArray.push(`"${resolveImagePath(mediaUrlPrefix, article.imageUrl)}"`);
    }

    if (article && article.mobileImageUrl) {
        imageArray.push(`"${resolveImagePath(mediaUrlPrefix, article.mobileImageUrl)}"`);
    }

    if (article && !article.imageUrl && !article.mobileImageUrl) {
        imageArray.push(`"${prefixStringWithBaseUrl(geotabShare)}"`);
    }

    return imageArray;
};

export const getHyperTocStructuredData = (content, fullUrl) => {
    const tocHeadingsRegexp = /<h([23]) id="([^"]+)">(?:<[^>]+>)*([^<]+)(?:<\/[^>]+>)*<\/h\1>/g;
    const tocEntries = [];
    let matches;

    while ((matches = tocHeadingsRegexp.exec(content)) !== null) {
        const id = matches[2];
        const text = matches[3].trim();

        tocEntries.push({
            '@type': 'HyperTocEntry',
            url: `${fullUrl}#${id}`,
            name: `${text}`,
        });
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'HyperToc',
        tocEntry: tocEntries,
    };
};
